import { useCallback, useMemo, useState } from 'react'
import { CalendarDateVO } from '../../../../../../../../../../domain/value-object/CalendarDateVO'
import { DatePosition, getDateForPercentX } from '../hooks/useDatePositions'
import {
  useCriticalPathContext,
  useCriticalPathDragContextOptional,
} from './CriticalPathContext'

interface UseCriticalPathDragInteractionParams {
  rowIndex: number
  isProcess: boolean
  wbsItemUuid: string | null
  barEndpoints: { startX: number | null; endX: number | null }
  datePositions: DatePosition[]
  isHovered: boolean
  isDependencyDragging: boolean
}

export const useCriticalPathDragInteraction = (
  params: UseCriticalPathDragInteractionParams
) => {
  const {
    rowIndex,
    isProcess,
    wbsItemUuid,
    barEndpoints,
    datePositions,
    isHovered,
    isDependencyDragging,
  } = params

  const criticalPathContext = useCriticalPathContext()
  const criticalPathDragContext = useCriticalPathDragContextOptional()

  const [isCriticalPathRowHovered, setIsCriticalPathRowHovered] =
    useState(false)

  // Pre-drag hover state (moving circle follows mouse on bar)
  const [preDragInfo, setPreDragInfo] = useState<{
    date: CalendarDateVO
    pixelX: number
    position: 'top' | 'bottom'
  } | null>(null)

  const isCriticalPathDragging =
    criticalPathDragContext?.dragState.isDragging ?? false
  const criticalPathDragFromRowIndex =
    criticalPathDragContext?.dragState.fromRowIndex ?? -1

  const isCriticalPathDropTarget =
    isCriticalPathDragging &&
    isProcess &&
    rowIndex !== criticalPathDragFromRowIndex &&
    rowIndex >= 0

  const showCriticalPathDropHighlight =
    isCriticalPathDropTarget &&
    isCriticalPathRowHovered &&
    barEndpoints.startX !== null

  const showPreDragCircle =
    criticalPathContext !== null &&
    isProcess &&
    isHovered &&
    !isDependencyDragging &&
    !isCriticalPathDragging &&
    preDragInfo !== null

  // Track mouse position on bar for pre-drag visual feedback.
  // Accepts raw coordinates and container rect so it can be called from
  // both React event handlers and native DOM event listeners.
  const handleBarMouseMove = useCallback(
    (clientX: number, clientY: number, containerRect: DOMRect) => {
      const relativeX = clientX - containerRect.left
      const percentX = (relativeX / containerRect.width) * 100

      // Only track within the scheduled bar range
      if (barEndpoints.startX !== null && barEndpoints.endX !== null) {
        if (percentX < barEndpoints.startX || percentX > barEndpoints.endX) {
          setPreDragInfo(null)
          return
        }
      } else {
        setPreDragInfo(null)
        return
      }

      const date = getDateForPercentX(datePositions, percentX)

      if (date) {
        const snappedPos = datePositions.find(p => p.date.isEqual(date))
        const snappedPercent = snappedPos
          ? snappedPos.x + snappedPos.width / 2
          : percentX
        const pixelX = (snappedPercent / 100) * containerRect.width
        const relativeY = clientY - containerRect.top
        const position = relativeY < containerRect.height / 2 ? 'top' : 'bottom'
        setPreDragInfo({ date, pixelX, position })
      } else {
        setPreDragInfo(null)
      }
    },
    [datePositions, barEndpoints]
  )

  // Drop target (mouseup on another process row's bar)
  const handleDropMouseUp = useCallback(
    (event: React.MouseEvent) => {
      if (
        !criticalPathDragContext ||
        !criticalPathContext ||
        !isProcess ||
        !wbsItemUuid
      ) {
        return
      }

      const dragState = criticalPathDragContext.dragState
      if (dragState.fromRowIndex === null || dragState.fromDate === null) return

      const container = (event.currentTarget as Element).closest(
        '.sevend-ag-cell-gantt-chart'
      )
      if (!container) return

      const containerRect = container.getBoundingClientRect()
      const relativeX = event.clientX - containerRect.left
      const percentX = (relativeX / containerRect.width) * 100

      const toDate = getDateForPercentX(datePositions, percentX)
      if (!toDate) return

      // Reject drops outside the scheduled bar range
      const toPos = datePositions.find(p => p.date.isEqual(toDate))
      if (toPos && barEndpoints.startX !== null && barEndpoints.endX !== null) {
        const toPercent = toPos.x + toPos.width / 2
        if (toPercent < barEndpoints.startX || toPercent > barEndpoints.endX) {
          criticalPathDragContext.endDrag()
          return
        }
      }

      const fromRowInfo = criticalPathContext.rowInfo.get(
        dragState.fromRowIndex
      )
      if (!fromRowInfo?.wbsItemUuid) return

      event.stopPropagation()
      event.preventDefault()

      criticalPathContext.addCriticalPathLink({
        fromRowIndex: dragState.fromRowIndex,
        fromDate: dragState.fromDate,
        fromWbsItemUuid: fromRowInfo.wbsItemUuid,
        fromProjectPlanUuid: fromRowInfo.projectPlanUuid,
        toRowIndex: rowIndex,
        toDate,
        toWbsItemUuid: wbsItemUuid,
        toProjectPlanUuid:
          criticalPathContext.rowInfo.get(rowIndex)?.projectPlanUuid,
      })

      criticalPathDragContext.endDrag()
    },
    [
      criticalPathDragContext,
      criticalPathContext,
      isProcess,
      wbsItemUuid,
      rowIndex,
      datePositions,
      barEndpoints,
    ]
  )

  // Drop target hover tracking (for mouse move on row during CP drag)
  const handleDropRowMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!criticalPathDragContext) return
      const container = (e.currentTarget as Element).closest(
        '.sevend-ag-cell-gantt-chart'
      )
      if (!container) return
      const containerRect = container.getBoundingClientRect()
      const relativeX = e.clientX - containerRect.left
      const percentX = (relativeX / containerRect.width) * 100
      const date = getDateForPercentX(datePositions, percentX)
      criticalPathDragContext.updateHoverDate(date)
    },
    [criticalPathDragContext, datePositions]
  )

  // Drop point position (snapped to date grid, restricted to bar range)
  const hoverDate = criticalPathDragContext?.dragState.hoverDate ?? null
  const dropPointPercent = useMemo(() => {
    if (!isCriticalPathRowHovered || !hoverDate) return null
    const pos = datePositions.find(p => p.date.isEqual(hoverDate))
    if (!pos) return null
    const percent = pos.x + pos.width / 2
    // Restrict to within the scheduled bar range
    if (barEndpoints.startX !== null && barEndpoints.endX !== null) {
      if (percent < barEndpoints.startX || percent > barEndpoints.endX) {
        return null
      }
    }
    return percent
  }, [isCriticalPathRowHovered, hoverDate, datePositions, barEndpoints])

  return {
    isCriticalPathDragging,
    isCriticalPathDropTarget,
    showCriticalPathDropHighlight,
    showPreDragCircle,
    preDragInfo,
    clearPreDragInfo: useCallback(() => setPreDragInfo(null), []),
    isCriticalPathRowHovered,
    setIsCriticalPathRowHovered,
    handleBarMouseMove,
    handleDropMouseUp,
    handleDropRowMouseMove,
    dropPointPercent,
    criticalPathDragContext,
  }
}
